import { HStack, List, Popover, Text, Whisper } from "rsuite";
import PropTypes from "prop-types";
import InfoIcon from "../../assets/img/info.svg";
import { formatCurrency } from "../../constants/utils";
import dayjs from "dayjs";

const formatDelta = (value, priceType, currency) => {
  if (priceType === "FLAT") {
    return formatCurrency(value, currency) || value;
  }
  return `${value}%`;
};

const resolveDeltaValue = (priceDelta, dayOfWeek) => {
  if (dayOfWeek && priceDelta?.[dayOfWeek] !== undefined) {
    return { single: priceDelta[dayOfWeek] };
  }
  const values = Object.values(priceDelta || {});
  if (!values.length) return null;
  const uniqueValues = [...new Set(values)];
  return uniqueValues.length === 1
    ? { single: uniqueValues[0] }
    : { min: Math.min(...values), max: Math.max(...values) };
};

const formatConfigDisplay = (
  config,
  dayOfWeek,
  currency,
  { spaced = false } = {}
) => {
  const { priceType, priceDelta, priceOperation } = config;
  const sign =
    priceOperation === "SUBTRACTION"
      ? spaced
        ? "- "
        : "-"
      : spaced
        ? "+ "
        : "+";

  const delta = resolveDeltaValue(priceDelta, dayOfWeek);
  if (!delta) return "";

  if (delta.single !== undefined) {
    return `${sign}${formatDelta(delta.single, priceType, currency)}`;
  }
  return `${sign}${formatDelta(delta.min, priceType, currency)} to ${sign}${formatDelta(delta.max, priceType, currency)}`;
};

const sortByOrder = (priceConfig) =>
  [...(priceConfig || [])].sort((a, b) => a.order - b.order);

const getDerivedRateDiscountDisplay = (priceConfig, dayOfWeek, currency) => {
  if (!priceConfig?.length) return "";
  return sortByOrder(priceConfig)
    .map((config) => formatConfigDisplay(config, dayOfWeek, currency))
    .filter(Boolean)
    .join(", ");
};

const getPriceAdjustmentChain = (priceConfig, dayOfWeek, currency) => {
  if (!priceConfig?.length) return "";
  const parts = sortByOrder(priceConfig)
    .map((config) =>
      formatConfigDisplay(config, dayOfWeek, currency, { spaced: true })
    )
    .filter(Boolean);
  return parts.length ? `base price ${parts.join(" ")}` : "";
};

const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];
const DAY_NAMES = {
  1: "Mon",
  2: "Tue",
  3: "Wed",
  4: "Thu",
  5: "Fri",
  6: "Sat",
  7: "Sun",
};

const isAllDays = (dow) =>
  dow?.length === 7 && ALL_DAYS.every((d) => dow.includes(d));

const formatDow = (dow) =>
  (dow || [])
    .slice()
    .sort((a, b) => a - b)
    .map((d) => DAY_NAMES[d])
    .join(", ");

const DerivedRatePopover = ({ derivedRate, dayOfWeek, currency }) => {
  const { name, restrictionConfig, priceConfig, contractDefinition } =
    derivedRate;
  const stayDates = restrictionConfig?.stayDates || [];
  const bookDates = restrictionConfig?.bookDates || [];
  const blackOutDates = restrictionConfig?.blackOutDates || [];
  const priceAdjustment = getPriceAdjustmentChain(
    priceConfig,
    dayOfWeek,
    currency
  );

  const showArrivalDow =
    restrictionConfig?.arrivalDow?.length &&
    !isAllDays(restrictionConfig.arrivalDow);
  const showStayDow =
    restrictionConfig?.stayDow?.length && !isAllDays(restrictionConfig.stayDow);

  const speaker = (
    <Popover title={<p className="popover-title">{name}</p>}>
      <List divider={false} size="xs">
        {priceAdjustment && (
          <List.Item>
            <HStack>
              <Text className="popover-content">Price Adjustment:</Text>
              <Text className="popover-content">{priceAdjustment}</Text>
            </HStack>
          </List.Item>
        )}
        {bookDates?.[0] && (
          <List.Item>
            <HStack>
              <Text className="popover-content">Booking Dates:</Text>
              <Text className="popover-content">
                {dayjs(bookDates[0].startDate).format("Do MMM YYYY")} -{" "}
                {dayjs(bookDates[0].endDate).format("Do MMM YYYY")}
              </Text>
            </HStack>
          </List.Item>
        )}
        {stayDates?.[0] && (
          <List.Item>
            <HStack>
              <Text className="popover-content">Stay Dates:</Text>
              <Text className="popover-content">
                {dayjs(stayDates[0].startDate).format("Do MMM YYYY")} -{" "}
                {dayjs(stayDates[0].endDate).format("Do MMM YYYY")}
              </Text>
            </HStack>
          </List.Item>
        )}
        {restrictionConfig?.minLeadTime > 0 && (
          <List.Item>
            <HStack>
              <Text className="popover-content">Min Lead Time:</Text>
              <Text className="popover-content">
                {restrictionConfig.minLeadTime}
              </Text>
            </HStack>
          </List.Item>
        )}
        {restrictionConfig?.minLos > 0 && (
          <List.Item>
            <HStack>
              <Text className="popover-content">Min Length of Stay:</Text>
              <Text className="popover-content">
                {restrictionConfig.minLos}
              </Text>
            </HStack>
          </List.Item>
        )}
        {restrictionConfig?.maxLos > 0 && (
          <List.Item>
            <HStack>
              <Text className="popover-content">Max Length of Stay:</Text>
              <Text className="popover-content">
                {restrictionConfig.maxLos}
              </Text>
            </HStack>
          </List.Item>
        )}
        {showArrivalDow && (
          <List.Item>
            <HStack>
              <Text className="popover-content">Arrival Days:</Text>
              <Text className="popover-content">
                {formatDow(restrictionConfig.arrivalDow)}
              </Text>
            </HStack>
          </List.Item>
        )}
        {showStayDow && (
          <List.Item>
            <HStack>
              <Text className="popover-content">Stay Days:</Text>
              <Text className="popover-content">
                {formatDow(restrictionConfig.stayDow)}
              </Text>
            </HStack>
          </List.Item>
        )}
        {blackOutDates.length > 0 &&
          blackOutDates.map((bo, idx) => (
            <List.Item key={`${bo.startDate}-${bo.endDate}`}>
              <HStack>
                <Text className="popover-content">
                  {idx === 0 ? "Blackout Dates:" : ""}
                </Text>
                <Text className="popover-content">
                  {dayjs(bo.startDate).format("Do MMM YYYY")} -{" "}
                  {dayjs(bo.endDate).format("Do MMM YYYY")}
                </Text>
              </HStack>
            </List.Item>
          ))}
        {contractDefinition?.mealPlanCode && (
          <List.Item>
            <HStack>
              <Text className="popover-content">Meal Plan:</Text>
              <Text className="popover-content">
                {contractDefinition.mealPlanCode}
              </Text>
            </HStack>
          </List.Item>
        )}
      </List>
    </Popover>
  );

  return (
    <Whisper placement="autoVerticalEnd" trigger="hover" speaker={speaker}>
      <img src={InfoIcon} className="info-icon" alt="info" />
    </Whisper>
  );
};

export { getDerivedRateDiscountDisplay };

DerivedRatePopover.propTypes = {
  derivedRate: PropTypes.object.isRequired,
  dayOfWeek: PropTypes.number,
  currency: PropTypes.string,
};

export default DerivedRatePopover;
